import React, { useEffect } from 'react';

import { usePathname } from 'next/navigation';
import * as Scroll from 'react-scroll';
import queryString from 'query-string';

import { ChevronLeft, ChevronRight } from '@assets/icons';
import CustomIcon from '@components/utils/CustomIcon';
import clean from '@utils/cleanObject';
import { useQueryParams } from '@hooks/useQueryParams';

import {
  CountTitle,
  NavigationButton,
  NavigationButtonTitle,
  NavigationContainer,
  PageButton,
  PageNumbersContainer,
  PaginationWrapper,
  SearchTitle,
  TextContainer,
} from './styled';
import { generatePageRange } from './utils';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  siblingCount?: number;
  onPageChange: (page: number) => void;
  searchTitle?: string;
  type: string;
  totalItemCount: number;
  showPaginationText?: boolean;
}

const Pagination = ({
  currentPage,
  totalPages,
  siblingCount = 2,
  onPageChange,
  searchTitle,
  type,
  totalItemCount,
  showPaginationText = true,
}: PaginationProps) => {
  const pageRange = generatePageRange(siblingCount, currentPage, totalPages);
  const pathname = usePathname();
  const query = useQueryParams();

  const handlePageChange = (page: number) => {
    if (page !== currentPage) {
      onPageChange(page);
    }
  };

  const getPageLink = (page: number) => {
    const { slug, ...nextQuery } = query;

    if (page === 1) {
      delete nextQuery.page;
    }

    const queryParamsToHref = queryString.stringify(
      clean(page === 1 ? { ...nextQuery } : { ...nextQuery, page: page })
    );

    return `${slug ?? pathname}${
      queryParamsToHref ? `?${queryParamsToHref}` : ''
    }`;
  };
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      onPageChange(1);
    }
  }, [currentPage, totalPages, onPageChange]);
  return (
    <PaginationWrapper>
      {showPaginationText ? (
        <TextContainer>
          <SearchTitle> {searchTitle}</SearchTitle> aramanızda
          <CountTitle> {totalItemCount}</CountTitle>{' '}
          {type === 'hotel' ? 'otel' : 'tur'},
          <CountTitle> {totalPages}</CountTitle> sayfada listelenmektedir.
        </TextContainer>
      ) : null}
      <NavigationContainer>
        <NavigationButton
          disabled={currentPage < 2}
          onClick={() => {
            if (currentPage < 2) return null;
            handlePageChange(currentPage - 1);
            Scroll.animateScroll.scrollToTop();
          }}
        >
          <CustomIcon
            icon={ChevronLeft}
            paletteColor={currentPage < 2 ? 'iron' : 'eclipse'}
            fontSize={14}
          />
          <NavigationButtonTitle disabled={currentPage < 2}>
            Önceki
          </NavigationButtonTitle>
        </NavigationButton>

        <PageNumbersContainer>
          {pageRange?.map((page) => (
            <PageButton
              key={page}
              isActive={page === currentPage}
              href={getPageLink(page)}
              prefetch={false}
              onClick={() => {
                handlePageChange(page);
                Scroll.animateScroll.scrollToTop();
              }}
            >
              {page}
            </PageButton>
          ))}
        </PageNumbersContainer>
        <NavigationButton
          disabled={currentPage === totalPages || currentPage > totalPages}
          onClick={() => {
            if (currentPage === totalPages || currentPage > totalPages)
              return null;
            handlePageChange(currentPage + 1);
            Scroll.animateScroll.scrollToTop();
          }}
        >
          <NavigationButtonTitle
            disabled={currentPage === totalPages || currentPage > totalPages}
          >
            Sonraki
          </NavigationButtonTitle>
          <CustomIcon
            icon={ChevronRight}
            paletteColor={
              currentPage === totalPages || currentPage > totalPages
                ? 'iron'
                : 'eclipse'
            }
            fontSize={14}
          />
        </NavigationButton>
      </NavigationContainer>
    </PaginationWrapper>
  );
};

export default Pagination;
